import { AxiosError } from '@mint-lib/api';
import type { TFunction } from '@mint-lib/i18n';
import { formCustomRTKQueryError } from '@mint-lib/rtk-query-client';

import {
  EMPTY_CELL_ERROR,
  LAST_ACTIVE_ROW_MESSAGE,
} from '../constants/myCampaigns.js';
import type { BaseQueryError } from '../types/shared.js';

export const getScrollbarWidth = () => {
  const div = document.createElement('div');
  div.style.cssText = `width: 50px;
  height: 50px;
  overflow-y: scroll;
  opacity: 0;
  visibility: hidden;`;
  document.body.append(div);

  const scrollbarWidth = div.offsetWidth - div.clientWidth;
  div.remove();

  return scrollbarWidth;
};

export const calcColumnHeaderWidth = ({
  isMask,
  isActionsField,
  scale,
  width,
  minWidth,
}: {
  isMask: boolean;
  isActionsField: boolean;
  scale: number;
  width: string | number | undefined;
  minWidth: number;
}) => {
  if (isMask) {
    return '100%';
  }

  const isInvalidWidthOrActionsField =
    isActionsField || !width || Number.isNaN(Number(width));

  if (isInvalidWidthOrActionsField) {
    return '0px';
  }

  return `${Math.max(Number(width), minWidth) * scale}px`;
};

const isHtmlPage = (data: string) => {
  const htmlRegex = /<(?:html|head|body)[^>]*>/i;

  return htmlRegex.test(data);
};

export const extractMessageFromError = (
  data: unknown,
  t?: TFunction,
): string => {
  if (typeof data === 'string' && !isHtmlPage(data)) {
    return data;
  }

  if (Array.isArray(data) && data.length) {
    return data[0];
  }

  if (typeof data === 'object' && data) {
    const key = Object.keys(data)[0];
    return key
      ? extractMessageFromError((data as Record<string, unknown>)[key], t)
      : '';
  }

  const defaultMessage = t?.('Sorry, something went wrong') || '';

  return defaultMessage;
};

type ErrorTransformerArgs = {
  error: BaseQueryError;
  skipSnackbarAppearance?: boolean;
};

export const transformValidationErrorResponse = ({
  error,
  skipSnackbarAppearance,
}: ErrorTransformerArgs) => {
  // status 400 means that it's a validation error
  const shouldHideSnackbar = error.status === 400 && skipSnackbarAppearance;

  if (shouldHideSnackbar) {
    return error;
  }

  const errorMessage = extractMessageFromError(error.data);

  return formCustomRTKQueryError({
    message:
      errorMessage === LAST_ACTIVE_ROW_MESSAGE
        ? EMPTY_CELL_ERROR
        : errorMessage,
    snackbarOptions: {
      skipAppearance: false,
      removable: true,
    },
  });
};

export type NestedKeyOf<T extends {}> = {
  [Key in keyof T & (string | number)]: T[Key] extends {}
    ? `${Key}` | `${Key}.${NestedKeyOf<T[Key]>}`
    : `${Key}`;
}[keyof T & (string | number)];

export type NestedValue<
  T extends {},
  K extends string,
> = K extends `${infer First}.${infer Rest}`
  ? First extends keyof T
    ? T[First] extends {}
      ? NestedValue<T[First], Rest>
      : never
    : never
  : K extends keyof T
    ? T[K]
    : never;

export function getNestedValue<T extends object, K extends NestedKeyOf<T>>(
  obj: T,
  key: K,
): NestedValue<T, K> {
  return key.split('.').reduce((acc: unknown, part: string) => {
    return acc && (acc as Record<string, unknown>)[part];
  }, obj as unknown) as NestedValue<T, K>;
}

export const isAxiosError = (error: unknown): error is AxiosError => {
  return (
    !!error &&
    typeof error === 'object' &&
    'name' in error &&
    error.name === 'AxiosError'
  );
};

export const localeCompare = (a: string, b: string) => {
  return a.localeCompare(b);
};
